import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, Loader, RefreshCw, Home } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentCallbackPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying'); // 'verifying', 'success', 'failed', 'error'
  const [message, setMessage] = useState('Verifying your payment...');
  const [paymentDetails, setPaymentDetails] = useState(null);

  const [countdown, setCountdown] = useState(null);

  useEffect(() => {
    const verifyPayment = async () => {
      const orderId = searchParams.get('order_id');
      
      if (!orderId) {
        setStatus('error');
        setMessage('Invalid payment callback. No order ID found.');
        return;
      }

      try {
        const token = localStorage.getItem('token');
        
        if (!token) {
          // User might have logged out or token expired
          // Still try to verify but redirect to login after
          setStatus('error');
          setMessage('Session expired. Please login to check your payment status.');
          setTimeout(() => navigate('/login'), 3000);
          return;
        }

        // Get order details from our database first
        const orderResponse = await axios.get(`${API}/payment-orders/${orderId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        const orderData = orderResponse.data;
        
        // Verify payment status with Cashfree
        const verifyResponse = await axios.post(`${API}/cashfree/verify-payment`, {
          order_id: orderId,
          payment_type: orderData.payment_type || 'team_registration',
          team_id: orderData.team_id,
          booking_id: orderData.booking_id
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (verifyResponse.data.success) {
          setStatus('success');
          setMessage('Payment successful! Your registration is confirmed.');
          setPaymentDetails({
            orderId: orderId,
            amount: orderData.amount,
            teamName: orderData.customer_name
          });
        } else {
          setStatus('failed');
          setMessage(verifyResponse.data.message || 'Payment verification failed.');
        }
      } catch (err) {
        console.error('Payment verification error:', err);
        const errorDetail = err.response?.data?.detail;
        
        if (typeof errorDetail === 'string' && errorDetail.includes('No successful payment')) {
          setStatus('failed');
          setMessage('Payment was not completed. Please try again.');
        } else {
          setStatus('error');
          setMessage('Unable to verify payment. Please check your dashboard for payment status.');
        }
      }
    };

    verifyPayment();
  }, [searchParams, navigate]);

  // Auto-redirect countdown after payment result
  useEffect(() => {
    if (status === 'success' || status === 'failed' || status === 'error') {
      setCountdown(5);
    }
  }, [status]);

  useEffect(() => {
    if (countdown === null || countdown <= 0) return;
    const timer = setTimeout(() => {
      if (countdown === 1) {
        navigate('/dashboard');
      } else {
        setCountdown(countdown - 1);
      }
    }, 1000);
    return () => clearTimeout(timer);
  }, [countdown, navigate]);

  const handleGoToDashboard = () => {
    navigate('/dashboard');
  };

  const handleRetryPayment = () => {
    navigate('/dashboard');
  };

  const handleGoHome = () => {
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8 text-center">
        {/* Status Icon */}
        <div className="mb-6">
          {status === 'verifying' && (
            <div className="w-20 h-20 mx-auto bg-blue-100 rounded-full flex items-center justify-center">
              <Loader className="w-10 h-10 text-blue-600 animate-spin" />
            </div>
          )}
          {status === 'success' && (
            <div className="w-20 h-20 mx-auto bg-green-100 rounded-full flex items-center justify-center animate-bounce-once">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
          )}
          {(status === 'failed' || status === 'error') && (
            <div className="w-20 h-20 mx-auto bg-red-100 rounded-full flex items-center justify-center">
              <XCircle className="w-10 h-10 text-red-600" />
            </div>
          )}
        </div>

        {/* Status Title */}
        <h1 className="text-2xl font-bold mb-2">
          {status === 'verifying' && 'Verifying Payment'}
          {status === 'success' && 'Payment Successful!'}
          {status === 'failed' && 'Payment Failed'}
          {status === 'error' && 'Verification Error'}
        </h1>

        {/* Message */}
        <p className="text-gray-600 mb-6">{message}</p>

        {/* Payment Details (on success) */}
        {status === 'success' && paymentDetails && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6 text-left">
            <h3 className="font-semibold text-green-800 mb-2">Payment Details</h3>
            <div className="space-y-1 text-sm text-green-700">
              <p><span className="font-medium">Order ID:</span> {paymentDetails.orderId}</p>
              <p><span className="font-medium">Amount:</span> ₹{paymentDetails.amount?.toLocaleString()}</p>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="space-y-3">
          {status === 'success' && (
            <button
              onClick={handleGoToDashboard}
              className="w-full py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
              data-testid="go-to-dashboard-btn"
            >
              Go to Dashboard
            </button>
          )}

          {status === 'failed' && (
            <>
              <button
                onClick={handleRetryPayment}
                className="w-full py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                data-testid="retry-payment-btn"
              >
                <RefreshCw size={18} />
                Retry Payment
              </button>
              <button
                onClick={handleGoToDashboard}
                className="w-full py-3 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors"
                data-testid="go-to-dashboard-secondary-btn"
              >
                Go to Dashboard
              </button>
            </>
          )}

          {status === 'error' && (
            <>
              <button
                onClick={handleGoToDashboard}
                className="w-full py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
                data-testid="check-dashboard-btn"
              >
                Check Dashboard
              </button>
              <button
                onClick={handleGoHome}
                className="w-full py-3 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors flex items-center justify-center gap-2"
                data-testid="go-home-btn"
              >
                <Home size={18} />
                Go Home
              </button>
            </>
          )}

          {status === 'verifying' && (
            <p className="text-sm text-gray-500">Please wait while we confirm your payment...</p>
          )}
        </div>

        {/* Help Text */}
        <div className="mt-6 pt-4 border-t border-gray-100">
          {countdown !== null && countdown > 0 && (
            <p className="text-sm text-blue-600 font-medium mb-2" data-testid="redirect-countdown">
              Redirecting to dashboard in {countdown}s...
            </p>
          )}
          <p className="text-xs text-gray-500">
            If you face any issues, please contact us at{' '}
            <a href="mailto:support@evalentx.com" className="text-blue-600 hover:underline">
              support@evalentx.com
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaymentCallbackPage;
